import React from "react";
import { X } from "lucide-react";
import type { OrderRefundSummary } from "../../../../../domain/types/tax-document";
import { resolveErrorMessage } from "../../../../../shared/utils/save-flow";
import { orderRefundApi } from "../../../../components/CompanyAdmin/TaxDocument/tax-document-api";

interface OrderRefundCardProps {
  companyId: number;
  orderId: number;
}

/** 오늘 날짜(로컬) YYYY-MM-DD. toISOString 은 UTC 라 자정 무렵 하루가 밀린다 */
const todayLocal = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * 무통장입금 주문의 환불 내역 + 부분 환불 등록.
 *
 * 돈은 시스템 밖(계좌 이체)에서 돌려주고, 여기서는 그 사실을 기록만 한다.
 * 증빙이 발급된 주문이면 서버가 환불 금액만큼 취소·수정 조정 건을 만든다.
 */
export const OrderRefundCard: React.FC<OrderRefundCardProps> = ({ companyId, orderId }) => {
  const [summary, setSummary] = React.useState<OrderRefundSummary | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [registering, setRegistering] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      setSummary(await orderRefundApi.summary(companyId, orderId));
      setLoadError(null);
    } catch (error) {
      setLoadError(resolveErrorMessage(error, "환불 내역을 불러오지 못했습니다."));
    }
  }, [companyId, orderId]);

  React.useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="bg-white border border-gray-200 rounded-[0.625rem] p-6 shadow-sm mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900 text-left">환불 내역</h3>
        {summary?.partialRefundAllowed && (
          <button
            onClick={() => setRegistering(true)}
            className="px-4 h-[36px] text-[13px] rounded-[6px] bg-[#222] text-white hover:bg-[#333]"
          >
            부분 환불 등록
          </button>
        )}
      </div>

      {loadError && <p className="text-[13px] text-[#888] text-left">{loadError}</p>}

      {summary && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <SummaryCell label="주문 금액" value={summary.orderTotalAmount} />
            <SummaryCell label="환불한 금액" value={summary.refundedTotal} />
            <SummaryCell label="환불 가능 금액" value={summary.refundableAmount} highlight />
          </div>

          {!summary.partialRefundAllowed && summary.notAllowedReason && (
            <div className="mb-4 rounded-[8px] border border-[#4285F4]/20 bg-[#EBF3FF] px-3 py-2 text-[13px] text-[#4285F4] text-left">
              {summary.notAllowedReason}
            </div>
          )}

          {summary.refunds.length === 0 ? (
            <p className="text-[13px] text-[#bbb] text-left">등록된 환불 내역이 없습니다.</p>
          ) : (
            <div className="border border-[#eee] rounded-[8px] overflow-hidden">
              <div className="grid grid-cols-[110px_120px_1fr] bg-[#fafafa] text-[12px] font-medium text-[#555] px-4 py-2 text-left">
                <span>환불일</span>
                <span>금액</span>
                <span>사유</span>
              </div>
              {summary.refunds.map((refund) => (
                <div
                  key={refund.id}
                  className="grid grid-cols-[110px_120px_1fr] px-4 py-2.5 text-[13px] text-[#222] border-t border-[#eee] text-left"
                >
                  <span>{refund.refundedAt}</span>
                  <span>₩{refund.amount.toLocaleString()}</span>
                  <span className="text-[#555] whitespace-pre-wrap break-all">{refund.reason}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {registering && summary && (
        <OrderRefundDialog
          companyId={companyId}
          orderId={orderId}
          refundableAmount={summary.refundableAmount}
          onClose={() => setRegistering(false)}
          onSaved={(next) => {
            setSummary(next);
            setRegistering(false);
          }}
        />
      )}
    </div>
  );
};

const SummaryCell: React.FC<{ label: string; value: number; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="rounded-[8px] bg-[#fafafa] px-4 py-3 text-left">
    <div className="text-[12px] text-[#888]">{label}</div>
    <div className={`text-[15px] font-bold mt-1 ${highlight ? "text-[#4285F4]" : "text-[#222]"}`}>
      ₩{value.toLocaleString()}
    </div>
  </div>
);

interface OrderRefundDialogProps {
  companyId: number;
  orderId: number;
  refundableAmount: number;
  onClose: () => void;
  onSaved: (next: OrderRefundSummary) => void;
}

const OrderRefundDialog: React.FC<OrderRefundDialogProps> = ({
  companyId,
  orderId,
  refundableAmount,
  onClose,
  onSaved,
}) => {
  const [amount, setAmount] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [refundedAt, setRefundedAt] = React.useState(todayLocal());
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const amountValue = Number(amount.replace(/,/g, ""));

  const handleSubmit = async () => {
    if (!Number.isInteger(amountValue) || amountValue <= 0) {
      setError("환불 금액을 입력해 주세요.");
      return;
    }
    if (amountValue > refundableAmount) {
      setError(`환불 가능 금액(₩${refundableAmount.toLocaleString()})을 넘을 수 없습니다.`);
      return;
    }
    if (!reason.trim()) {
      setError("환불 사유를 입력해 주세요.");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      onSaved(
        await orderRefundApi.create(companyId, orderId, {
          amount: amountValue,
          reason: reason.trim(),
          refundedAt: refundedAt || undefined,
        }),
      );
    } catch (e) {
      setError(resolveErrorMessage(e, "부분 환불 등록에 실패했습니다."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/40">
      <div className="w-[480px] rounded-[12px] bg-white shadow-lg overflow-hidden flex flex-col text-left">
        <div className="flex items-center justify-between p-5 border-b border-[#eee]">
          <h3 className="text-[16px] font-bold text-[#222]">부분 환불 등록</h3>
          <button onClick={onClose} className="text-[#999] hover:text-[#666]" aria-label="닫기">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-4">
          {error && (
            <div className="rounded-[8px] border border-[#4285F4]/20 bg-[#EBF3FF] px-3 py-2 text-[13px] text-[#4285F4]">
              {error}
            </div>
          )}

          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-[#555]">환불 금액</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={refundableAmount}
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setError(null);
              }}
              placeholder={`최대 ${refundableAmount.toLocaleString()}원`}
              className="h-[40px] px-3 text-[14px] border border-[#e0e0e0] rounded-[8px] focus:border-[#4285F4] focus:outline-none"
            />
            <span className="text-[12px] text-[#888]">환불 가능 금액 ₩{refundableAmount.toLocaleString()}</span>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-[#555]">환불일</span>
            <input
              type="date"
              value={refundedAt}
              onChange={(e) => setRefundedAt(e.target.value)}
              className="h-[40px] px-3 text-[14px] border border-[#e0e0e0] rounded-[8px] focus:border-[#4285F4] focus:outline-none"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-[#555]">환불 사유</span>
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setError(null);
              }}
              rows={3}
              maxLength={500}
              placeholder="예: 일부 수량 불량으로 차액 환불"
              className="px-3 py-2 text-[14px] border border-[#e0e0e0] rounded-[8px] focus:border-[#4285F4] focus:outline-none"
            />
          </label>

          <p className="text-[12px] text-[#888] leading-relaxed">
            증빙(현금영수증·세금계산서)이 발급된 주문이면 환불 금액만큼 취소·수정 건이 만들어지며, 세금계산서/현금영수증
            관리에서 확인 후 처리합니다.
          </p>
        </div>

        <div className="p-4 border-t border-[#eee] flex justify-end gap-2">
          <button
            disabled={busy}
            onClick={onClose}
            className="px-3 h-[36px] text-[13px] rounded-[6px] bg-white text-[#222] border border-[#222] hover:bg-[#f5f5f5] disabled:opacity-50"
          >
            취소
          </button>
          <button
            disabled={busy}
            onClick={handleSubmit}
            className="px-4 h-[36px] text-[13px] rounded-[6px] bg-[#222] text-white hover:bg-[#333] disabled:bg-[#ccc]"
          >
            {busy ? "저장 중..." : "등록"}
          </button>
        </div>
      </div>
    </div>
  );
};
